import { ReactElement } from 'react';
import { Flex, Icon, Checkbox, Button, Popover, PopoverTrigger, PopoverContent } from '@cad-arc';
import { CheckboxMultiselect } from '../../common/components/checkbox-multiselect';
import { WebMap } from '../constructors';
import { useMapLayersDropdownUtils } from '../hooks/use-map-layers-dropdown-utils';
import { useSetMapLayersVisibility } from '../hooks/use-set-map-layers-visibility';
import { useTakeLayersToTopOfMap } from '../hooks/use-take-layers-to-top-of-map';
import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { LayerId } from '../types';
import { GenericObject } from '../../common/types';

type EsriMapLayersDropdownProps = {
    webMap: WebMap | undefined;
    commonPopoverButtonStyles: GenericObject<string | number>;
    visibleLayersIds: LayerId[];
    setVisibleLayersIds: (layerIds: LayerId[]) => void;
    onCloseLayersPopover?: () => void;
};

export const EsriMapLayersDropdown = ({
    webMap,
    commonPopoverButtonStyles,
    visibleLayersIds,
    setVisibleLayersIds,
    onCloseLayersPopover,
}: EsriMapLayersDropdownProps): ReactElement | null => {
    const { layersDisplayedInDropdown, checkboxesOptions } = useMapLayersDropdownUtils(webMap);
    const allLayersVisible = visibleLayersIds.length === checkboxesOptions.length;
    const someLayersVisible = visibleLayersIds.length > 0 && !allLayersVisible;
    useSetMapLayersVisibility(webMap, layersDisplayedInDropdown, visibleLayersIds);
    useTakeLayersToTopOfMap(
        [
            FEATURE_LAYERS_PROPERTIES.EVENTS.layerKey,
            FEATURE_LAYERS_PROPERTIES.UNITS.layerKey,
            FEATURE_LAYERS_PROPERTIES.STATIONS.layerKey,
            FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.layerKey,
            FEATURE_LAYERS_PROPERTIES.POLYLINES.layerKey,
        ],
        webMap
    );

    const handleSelectAllLayers = () => {
        if (allLayersVisible) {
            setVisibleLayersIds([]);
        } else {
            setVisibleLayersIds(layersDisplayedInDropdown?.map((layer) => layer?.id ?? '') ?? []);
        }
    };

    const clearLayers = () => {
        setVisibleLayersIds([]);
    };

    const handleChange = (layerIds: LayerId[]) => {
        setVisibleLayersIds(layerIds);
    };

    if (!layersDisplayedInDropdown || layersDisplayedInDropdown.length === 0) {
        return null;
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="solid"
                    style={commonPopoverButtonStyles}
                    data-testid="MAP_LAYERS_BUTTON"
                >
                    <Icon icon="Layers" /> Layers
                </Button>
            </PopoverTrigger>
            <PopoverContent
                data-testid="MAP_LAYERS_DROPDOWN"
                onEscapeKeyDown={onCloseLayersPopover}
                onInteractOutside={onCloseLayersPopover}
            >
                <Flex
                    backgroundColor="surface.foreground"
                    maxHeight="300px"
                    maxWidth="280px"
                    overflowY="auto"
                    flexDirection="column"
                    alignItems="flex-start"
                    position="relative"
                >
                    <Flex
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        gap="25px"
                        position="sticky"
                        top={0}
                        backgroundColor="surface.foreground"
                        zIndex={1}
                        paddingBottom="5px"
                    >
                        <Checkbox
                            isChecked={allLayersVisible}
                            isIndeterminate={someLayersVisible}
                            onChange={handleSelectAllLayers}
                            data-testid="MAP_LAYERS_SELECT_ALL_CHECKBOX"
                        >
                            Select all
                        </Checkbox>
                        {visibleLayersIds.length > 0 && (
                            <Button
                                variant="ghost"
                                onClick={clearLayers}
                                style={{
                                    padding: 0,
                                    height: 0,
                                    marginRight: '5px',
                                }}
                                data-testid="MAP_LAYERS_CLEAR_BUTTON"
                            >
                                Clear
                            </Button>
                        )}
                    </Flex>
                    <CheckboxMultiselect
                        value={visibleLayersIds}
                        options={checkboxesOptions}
                        onChange={handleChange}
                        columns={1}
                    />
                </Flex>
            </PopoverContent>
        </Popover>
    );
};
